import imageCompression from "browser-image-compression";
import * as DocumentPicker from "expo-document-picker";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import React from "react";
import { log } from "../../ui/log";
import { storage } from "../firebaseInit";
import { trackEvent } from "../trackEvent";
import { usePerformance } from "../usePerformance";
import { getImageInfo } from "../utils";

const STORAGE_PATH = "media";

export function useUploadMedia(getStoragePathSuffix: () => Promise<string>) {
  const performanceMeasure = usePerformance("UPLOAD_MEDIA");

  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [file, setFile] = React.useState<File | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);

  const uploadFile = React.useCallback(
    async (uploadFileParam?: File) => {
      const storagePathSuffix = await getStoragePathSuffix();

      const uploadFile = uploadFileParam ?? file;

      if (!storagePathSuffix) {
        log.dev("Error - no storagePathSuffix set");
        throw Error();
      }

      if (!uploadFile) {
        log.dev("Error - no file selected");
        throw Error();
      }

      performanceMeasure.start();
      setIsUploading(true);

      let url = "";

      try {
        // Create a reference to 'images/mountains.jpg'
        const storageRef = ref(storage, `${STORAGE_PATH}/${storagePathSuffix}`);

        const snapshot = await uploadBytes(storageRef, uploadFile);
        log.dev("Uploaded file!", snapshot);

        url = await getDownloadURL(snapshot.ref); // include token for cache busts.split("&")[0];

        log.dev("got url..", url);
        log.dev("sizes", getImageInfo(url));

        performanceMeasure.stop();

        trackEvent("media_uploaded");
      } catch (e) {
        console.error("Got invalid select file", e);
        trackEvent("media_upload_error");
      }

      setImageSrc(null);
      setIsUploading(false);

      return url;
    },
    [getStoragePathSuffix, file, performanceMeasure],
  );

  const selectFile = React.useCallback(async () => {
    if (isUploading) {
      return;
    }

    const result = await DocumentPicker.getDocumentAsync({
      type: "image/*",
    });

    // Only web compatible?
    const file = result.output?.[0];

    if (!file) {
      log.dev("No file found");
      return;
    }

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 414 * 2,
      useWebWorker: true,
      fileType: "image/webp", // Convert to WebP
    };
    try {
      const compressedFile = await imageCompression(file, options);
      console.log(
        "compressedFile instanceof Blob",
        compressedFile instanceof Blob,
      ); // true
      console.log(
        `compressedFile size ${compressedFile.size / 1024 / 1024} MB`,
      ); // smaller than maxSizeMB

      const firstImageSrc = result.assets?.[0].uri ?? "";

      setFile(compressedFile);
      setImageSrc(firstImageSrc);

      return { compressedFile, imageSrc: firstImageSrc };
    } catch (error) {
      console.log(error);
      throw Error("could not compress file");
    }
  }, [isUploading]);

  const abort = React.useCallback(() => {
    setIsUploading(false);
    setImageSrc(null);
    setFile(null);
  }, []);

  return { selectFile, isUploading, imageSrc, uploadFile, abort };
}
